import { useEffect, useState } from "react";
import { get_kline } from "api/v4/market";
import { addKlineChannel } from "utils/socket/klineChannel";
import { parseSymbolBaseQuote } from "./convertKlineSymbol";

/** TradingView interval → kline API interval（与 CFD 行情一致） */
export const CONVERT_KLINE_INTERVAL_TO_API = {
  "1": "1m",
  "3": "3m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1h",
  "120": "2h",
  "240": "4h",
  "360": "6h",
  "480": "8h",
  "720": "12h",
  "1D": "1d",
  "3D": "3d",
  "1W": "1w",
  "1M": "1M",
};

export function getConvertKlineOptionKey(interval, chartType = 3) {
  return `${interval || "1"}_${chartType ?? 3}`;
}

export function resolveConvertKlinePeriodLabel(interval, chartType, t) {
  if (!interval) return "";
  if (chartType === 3 && interval === "1") return t("trade.klineTime");
  if (interval === "15") return `15${t("trade.klineMin")}`;
  if (interval === "240") return `4${t("trade.klineHour")}`;
  if (interval === "1D") return `1${t("trade.klineDay")}`;

  const labelMap = {
    "1": `1${t("trade.klineMin")}`,
    "3": `3${t("trade.klineMin")}`,
    "5": `5${t("trade.klineMin")}`,
    "30": `30${t("trade.klineMin")}`,
    "60": `1${t("trade.klineHour")}`,
    "120": `2${t("trade.klineHour")}`,
    "360": `6${t("trade.klineHour")}`,
    "480": `8${t("trade.klineHour")}`,
    "720": `12${t("trade.klineHour")}`,
    "3D": `3${t("trade.klineDay")}`,
    "1W": `1${t("trade.klineWeek")}`,
    "1M": `1${t("trade.klineMonth")}`,
  };
  return labelMap[interval] || interval;
}

export function parseKlinePairCodes(symbol) {
  const { base, quote } = parseSymbolBaseQuote(symbol);
  return {
    base: base ? base.toUpperCase() : "",
    quote: quote ? quote.toUpperCase() : "",
  };
}

/** 将 K 线收盘价（base/quote）换算为 1 From ≈ X To 的 X */
export function computeConvertFromToRate(fromCurrency, toCurrency, klineSymbol, closePrice) {
  const { base, quote } = parseKlinePairCodes(klineSymbol);
  const from = (fromCurrency || "").toUpperCase();
  const to = (toCurrency || "").toUpperCase();
  if (!from || !to || !base || !quote) return null;

  const rate = parseFloat(String(closePrice ?? "").replace(/,/g, ""));
  if (!Number.isFinite(rate) || rate === 0) return null;

  if (from === base && to === quote) return rate;
  if (from === quote && to === base) return 1 / rate;
  return null;
}

/** 仅去掉小数部分末尾多余的 0；整数如 "100" 不做截断 */
export function trimTrailingZeros(value) {
  const s = String(value ?? "").replace(/,/g, "").trim();
  if (!s) return "";
  if (!/^-?\d+(\.\d+)?$/.test(s)) return "";
  if (!s.includes(".")) return s;
  return s.replace(/\.?0+$/, "") || "0";
}

export function formatKlineClosePrice(close) {
  return trimTrailingZeros(close);
}

/** Convert 页 K 线 / To 到账 / 确认弹窗等统一展示精度 */
export const CONVERT_DISPLAY_DECIMALS = 6;

/** Convert K 线 / Price / 到账展示：默认最多 6 位小数，末尾多余 0 省略 */
export function formatConvertKlineDisplayPrice(value, fractionDigits = CONVERT_DISPLAY_DECIMALS) {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return "";
  return trimTrailingZeros(n.toFixed(fractionDigits));
}

export function formatInvertedKlineRate(rate, fractionDigits = CONVERT_DISPLAY_DECIMALS) {
  const n = parseFloat(String(rate ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n) || n === 0) return "";
  return trimTrailingZeros((1 / n).toFixed(fractionDigits));
}

/** 试算 targetAmount 按展示精度格式化后为 0（含过小被截断为 0 的情况） */
export function isConvertReceiveAmountDisplayZero(targetAmount, fractionDigits = CONVERT_DISPLAY_DECIMALS) {
  const raw = parseFloat(String(targetAmount ?? "").replace(/,/g, ""));
  if (!Number.isFinite(raw) || raw <= 0) return true;
  const formatted = formatConvertKlineDisplayPrice(String(targetAmount), fractionDigits);
  if (!formatted || formatted === "0") return true;
  const displayed = parseFloat(formatted);
  return !Number.isFinite(displayed) || displayed <= 0;
}

/** 无 K 线时：用 exchangeRate 换算 1 From ≈ X To 中的 X（与 Confirm Order Rate 兜底一致） */
export function formatConvertFallbackRatePrice(exchangeRate, convertMode) {
  if (exchangeRate == null || exchangeRate === "") return "";
  const n = parseFloat(String(exchangeRate).replace(/,/g, ""));
  if (!Number.isFinite(n) || n === 0) return "";
  if (convertMode === "buy") {
    return formatConvertKlineDisplayPrice(String(1 / n));
  }
  return formatConvertKlineDisplayPrice(String(n));
}

export function fetchKlineClosePrice(klineSymbol, interval) {
  if (!klineSymbol) return Promise.resolve("");
  return get_kline({
    params: {
      symbol: klineSymbol,
      interval: CONVERT_KLINE_INTERVAL_TO_API[interval] || "1d",
      limit: 1,
      version: 2,
    },
    isAutoPopError: false,
  })
    .then((data) => {
      if (!Array.isArray(data) || !data.length) return "";
      const latest = data[data.length - 1];
      const close = latest?.c ?? latest?.close;
      return close != null ? formatKlineClosePrice(close) : "";
    })
    .catch(() => "");
}

export function useConvertKlineClosePrice(klineSymbol, interval) {
  const [price, setPrice] = useState("");

  useEffect(() => {
    if (!klineSymbol) {
      setPrice("");
      return undefined;
    }

    let cancelled = false;
    const apiInterval = CONVERT_KLINE_INTERVAL_TO_API[interval] || "1m";

    fetchKlineClosePrice(klineSymbol, interval).then((value) => {
      if (!cancelled && value) setPrice(value);
    });

    const wsEvent = `kline@${klineSymbol},${apiInterval}`;
    const handleWsKline = (data) => {
      if (cancelled || !data) return;
      const close = data?.c ?? data?.close;
      if (close != null && close !== "") {
        setPrice(String(close));
      }
    };

    const unsubscribe = addKlineChannel(wsEvent, handleWsKline);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [klineSymbol, interval]);

  return price;
}
